<template>
  <div class="vehicle-detail">
    <PageComponentsCarDetailBreadCrumbs class="mb-6" />

    <div class="vehicle-detail__pictures h-[512] grid grid-cols-[70%_30%] xl:grid-cols-[65%_35%] 2xl:grid-cols-[60%_40%] gap-4" v-if="vehicleImages.length">
      <div class="col-span-1 h-[512px] relative">
        <SharedPicture
          :media="vehicleImages[0]"
          fetchpriority="high"
          loading="eager"
          :class="{ hidden: mainSliderInitialised, 'vehicle-detail__pictures-item vehicle-detail__pictures-item--md-lg': !mainSliderInitialised }"
          imageClass="w-full !h-auto overflow-hidden !object-contain object-center !rounded-sm"
          class="h-full w-auto !aspect-[303/228] !object-contain object-center block"
          height="383"
          width="510"
          format="webp"
          :quality="85"
        />
        <SharedPicture
          :media="vehicleImages[0]"
          fetchpriority="high"
          loading="eager"
          :class="{ hidden: mainSliderInitialised, 'vehicle-detail__pictures-item vehicle-detail__pictures-item--lg-xxl': !mainSliderInitialised }"
          imageClass="w-full !h-auto overflow-hidden !object-contain object-center !rounded-sm"
          class="h-full w-auto !aspect-[303/228] !object-contain object-center block"
          height="512"
          width="680"
          format="webp"
          :quality="85"
        />
        <client-only>
          <swiper-container
            :class="{ hidden: !mainSliderInitialised }"
            class="h-full overflow-hidden w-full block"
            space-between="30"
            @swiperactiveindexchange="handleMainSlideChange"
            @swiperinit="handleMainSliderInit"
          >
            <swiper-slide v-for="(image, i) in vehicleImages" :key="image?.url || i" :lazy="i > 4" @click="openLightbox(i)">
              <SharedPicture
                :fetchpriority="i < 4 ? 'high' : 'low'"
                :loading="i === 0 ? 'eager' : 'lazy'"
                :media="image"
                imageClass="w-full !h-auto overflow-hidden !object-contain object-center !rounded-sm"
                class="h-full w-auto !aspect-[303/228] !object-contain object-center block vehicle-detail__pictures-item vehicle-detail__pictures-item--md-lg"
                height="383"
                width="510"
                format="webp"
                :quality="85"
              />
              <SharedPicture
                :fetchpriority="i < 4 ? 'high' : 'low'"
                :loading="i === 0 ? 'eager' : 'lazy'"
                :media="image"
                imageClass="!h-full !w-auto overflow-hidden !object-contain object-center !rounded-sm"
                class="!h-full !w-auto mx-auto !aspect-[303/228] !object-contain object-center block vehicle-detail__pictures-item vehicle-detail__pictures-item--lg-xxl"
                height="512"
                width="680"
                format="webp"
                :quality="85"
              />
            </swiper-slide>
          </swiper-container>
        </client-only>

        <client-only>
          <VueEasyLightbox v-if="lightboxVisible" :imgs="lightboxImages" :index="lightboxIndex" :visible="lightboxVisible" @hide="onLightboxHide" teleport="#floating-actions-container" />
        </client-only>

        <SharedIconButton
          :aria-label="t('swiper.previous')"
          :title="t('swiper.previous')"
          variant="accent"
          @click="() => swiper?.slidePrev()"
          icon="arrow-left"
          :disabled="activeIndex === 0"
          class="w-10 h-10 absolute bottom-0 translate-y-1/2 left-2 z-10 print:!hidden"
          ref="swiperBackButton"
          ><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12.7069 17.293L8.41394 13H17.9999V11H8.41394L12.7069 6.70697L11.2929 5.29297L4.58594 12L11.2929 18.707L12.7069 17.293Z" fill="currentColor" />
          </svg>
        </SharedIconButton>
        <SharedIconButton
          :aria-label="t('swiper.next')"
          :title="t('swiper.next')"
          variant="accent"
          @click="() => swiper?.slideNext()"
          icon="arrow-right"
          class="w-10 h-10 absolute bottom-0 translate-y-1/2 right-2 z-10 print:!hidden"
          ref="swiperNextButton"
          :disabled="activeIndex === vehicleImages.length - 1"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M11.2931 17.293L15.5861 13H6.00006V11H15.5861L11.2931 6.70697L12.7071 5.29297L19.4141 12L12.7071 18.707L11.2931 17.293Z" fill="currentColor" />
          </svg>
        </SharedIconButton>
      </div>
      <div
        ref="secondarySlider"
        class="vehicle-detail__images-secondary-slider col-span-1 w-full grid grid-cols-1 xl:grid-cols-2 grid-rows-[repeat(auto-fill,minmax(160px,1fr))] gap-4 max-h-[512px] overflow-y-auto h-full"
        tabindex="0"
        role="region"
        aria-label="Fahrzeugbilder Galerie"
        @keydown="handleSecondarySliderKeydown"
      >
        <SharedPicture
          v-for="(image, i) in vehicleImages"
          :key="image?.url || i"
          :media="image"
          imageClass="w-full h-full object-cover object-center block rounded-sm"
          class="w-full h-[160px] object-cover object-center block col-span-1 border border-accent/0 cursor-pointer rounded-sm"
          :class="{ 'border-accent/100': activeIndex === i }"
          @click="changeMainSlide(i)"
          height="158"
          width="224"
          format="webp"
          :quality="80"
          :loading="i < 7 ? 'eager' : 'lazy'"
          :fetchpriority="i < 7 ? 'high' : 'low'"
          :data-index="i"
        />
      </div>
    </div>

    <div class="vehicle-detail__header flex items-end justify-between mt-12">
      <PageComponentsCarDetailTitle />
      <PageComponentsCarDetailPrice />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { GraphQLCarDetail } from '~/types/graphql';
  import type { Swiper } from 'swiper/types';
  const { t } = useI18n();
  const carDetail = inject<GraphQLCarDetail>('carDetail');
  const vehicleImages = computed(() => useVehicleImages(carDetail ?? {}));
  const secondarySlider = ref<HTMLElement | null>(null);

  const mainSliderInitialised = ref(false);
  const activeIndex = ref(0);
  const swiper = ref<Swiper | null>(null);
  const handleMainSliderInit = (e: CustomEvent<Swiper[]>) => {
    if (e.detail[0]) {
      swiper.value = e.detail[0];
    }
    mainSliderInitialised.value = true;
  };
  const handleMainSlideChange = (e: CustomEvent<Swiper[]>) => {
    if (e.detail[0]) {
      activeIndex.value = e.detail[0].activeIndex;
    }
  };

  const changeMainSlide = (index: number) => {
    if (swiper.value) {
      activeIndex.value = index;
      swiper.value.slideTo(index);
    }
  };

  // move secondary slider active item into view
  watch(activeIndex, newIndex => {
    if (secondarySlider.value) {
      const item = secondarySlider.value.querySelector(`[data-index="${newIndex}"]`);
      if (item) {
        item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }
  });

  const handleSecondarySliderKeydown = (e: KeyboardEvent) => {
    if (!secondarySlider.value) return;

    const scrollAmount = 160; // Match the height of each image item
    const { scrollTop, scrollHeight, clientHeight } = secondarySlider.value;

    switch (e.key) {
      case 'ArrowDown':
      case 'PageDown':
        e.preventDefault();
        secondarySlider.value.scrollBy({ top: scrollAmount, behavior: 'smooth' });
        break;
      case 'ArrowUp':
      case 'PageUp':
        e.preventDefault();
        secondarySlider.value.scrollBy({ top: -scrollAmount, behavior: 'smooth' });
        break;
      case 'Home':
        e.preventDefault();
        secondarySlider.value.scrollTo({ top: 0, behavior: 'smooth' });
        break;
      case 'End':
        e.preventDefault();
        secondarySlider.value.scrollTo({ top: scrollHeight, behavior: 'smooth' });
        break;
    }
  };

  // Lazy-load the lightbox — its chunk (JS + CSS) is fetched on first open, not on page load.
  const VueEasyLightbox = defineAsyncComponent(() => import('vue-easy-lightbox'));

  const lightboxIndex = ref(0);
  const lightboxVisible = ref(false);

  const lightboxImages = ref<string[]>([]);

  const { lockScroll, unlockScroll } = useScrollLock();

  const config = useRuntimeConfig();

  const openLightbox = (index: number) => {
    if (!vehicleImages.value.length) return;

    // zero out the images array
    const images = <string[]>[];

    vehicleImages.value.forEach(image => {
      const imgSrc = new URL(image.url, config.public.strapi.url).toString();
      if (imgSrc) {
        images.push(imgSrc);
      }
    });

    lightboxImages.value = images;

    lockScroll();
    lightboxIndex.value = index;
    lightboxVisible.value = true;
  };

  const onLightboxHide = () => {
    lightboxVisible.value = false;
    unlockScroll();
  };
</script>

<style lang="scss" scoped>
  .vehicle-detail {
    &__title {
      max-width: 800px;
      font-family: Chivo;
      font-size: 48px;
      font-style: normal;
      font-weight: 600;
      line-height: 120%; /* 57.6px */
      letter-spacing: -0.48px;
    }

    &__images-secondary-slider {
      @apply scroll-smooth scroll-m-2 pr-1;

      @screen 2xl {
        @apply pr-3;
      }

      &::-webkit-scrollbar {
        @apply w-[6px] h-2;
        @apply bg-neutral-light;
        @apply rounded-full;
      }

      &::-webkit-scrollbar-track {
        @apply bg-transparent;
      }

      &::-webkit-scrollbar-thumb {
        @apply bg-neutral-dark;
        @apply rounded-full;

        &:hover {
          @apply brightness-90;
        }
      }
    }
    // custom scrollbar - hide arrows and show 2px grey slider
    //   &::-webkit-scrollbar {
    //     width: 6px;
    //   }

    //   &::-webkit-scrollbar-track {
    //     background: transparent;
    //   }

    //   &::-webkit-scrollbar-thumb {
    //     background: #6b7280;
    //     border-radius: 1px;
    //   }

    //   &::-webkit-scrollbar-thumb:hover {
    //     background: #4b5563;
    //   }

    //   // Hide scrollbar arrows
    //   &::-webkit-scrollbar-button {
    //     display: none;
    //     width: 0;
    //     height: 0;
    //   }

    //   &::-webkit-scrollbar-button:start {
    //     display: none;
    //   }

    //   &::-webkit-scrollbar-button:end {
    //     display: none;
    //   }

    //   // Firefox scrollbar
    //   scrollbar-width: thin;
    //   scrollbar-color: #6b7280 transparent;
    // }
  }
</style>

<style lang="scss">
  .vehicle-detail__pictures-item {
    &--md-lg {
      display: block !important;
      @media (min-width: 1040px) {
        display: none !important;
      }
    }
    &--lg-xxl {
      display: none !important;
      @media (min-width: 1040px) {
        display: block !important;
      }
    }
  }
</style>
